import { USERS_URL, UPLOAD_URL } from "../constants.js"
import { apiSlice } from "./apiSlice.js"

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/login`,
        method: "POST",
        body: data,
      }),
    }),
    register: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}`,
        method: "POST",
        body: data,
      }),
    }),
    verifyEmail: builder.mutation({
      query: (token) => ({
        url: `${USERS_URL}/verify-email?token=${encodeURIComponent(token)}`,
        method: "GET",
      }),
    }),
    resendEmailVerification: builder.mutation({
      query: (email) => ({
        url: `${USERS_URL}/verify-email/resend`,
        method: "POST",
        body: { email },
      }),
    }),
    forgotPassword: builder.mutation({
      query: (email) => ({
        url: `${USERS_URL}/forgot-password`,
        method: "POST",
        body: { email },
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: `${USERS_URL}/reset-password`,
        method: "POST",
        body: { token, password },
      }),
    }),
    validateResetPasswordToken: builder.query({
      query: (token) => ({
        url: `${USERS_URL}/reset-password/validate?token=${encodeURIComponent(token)}`,
        method: "GET",
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/logout`,
        method: "POST",
      }),
    }),
    upgradeUserToCreator: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/upgrade`,
        method: "PUT",
        body: data,
      }),
    }),
    profile: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/profile`,
        method: "PUT",
        body: data,
      }),
    }),
    closeAccount: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/profile/close`,
        method: "PUT",
      }),
    }),
    getProfile: builder.query({
      query: () => ({
        url: `${USERS_URL}/profile`,
      }),
      keepUnusedDataFor: 5,
      invalidatesTags: ["Users"], // This will refresh user data if necessary
    }),
    updateCreator: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/profile/creator`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Users"], // This will refresh user data if necessary
    }),
    saveBusinessMethod: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/profile/creator-method`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["User"],
    }),
    deleteBusinessStripeMethod: builder.mutation({
      query: () => ({
        url: `${USERS_URL}/profile/business-delete-method/stripe`,
        method: "DELETE",
      }),
    }),
    setDefaultBusinessMethod: builder.mutation({
      query: ({ type }) => ({
        url: `${USERS_URL}/profile/business/default`,
        method: "PUT",
        body: { type },
      }),
    }),
    getUserNotifications: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/profile/notifications?userId=${userId}`,
        method: "GET",
      }),
      providesTags: ["Notifications"],
    }),
    getUserPurchaseHistory: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/profile/purchase-history?userId=${userId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 5,
    }),
    getUserWishlist: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/profile/wishlist?userId=${userId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 5,
      providesTags: ["Wishlist"],
    }),
    addProductToWishlist: builder.mutation({
      query: (productId) => ({
        url: `${USERS_URL}/profile/wishlist/${productId}`,
        method: "POST",
      }),
      invalidatesTags: ["Wishlist"],
    }),
    removeProductFromWishlist: builder.mutation({
      query: (productId) => ({
        url: `${USERS_URL}/profile/wishlist/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Wishlist"],
    }),
    followCreator: builder.mutation({
      query: (creatorId) => ({
        url: `${USERS_URL}/profile/follow/${creatorId}`,
        method: "POST",
      }),
      invalidatesTags: ["Follow"],
    }),
    unfollowCreator: builder.mutation({
      query: (creatorId) => ({
        url: `${USERS_URL}/profile/follow/${creatorId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Follow"],
    }),
    getFollowingCreators: builder.query({
      query: () => ({
        url: `${USERS_URL}/profile/following`,
        method: "GET",
      }),
      providesTags: ["Follow"],
      keepUnusedDataFor: 5,
    }),
    getCreatorFollowers: builder.query({
      query: () => ({
        url: `${USERS_URL}/profile/followers`,
        method: "GET",
      }),
      providesTags: ["Follow"],
      keepUnusedDataFor: 5,
    }),
    getUserPurchaseProduct: builder.query({
      query: ({ userId, productId }) => ({
        url: `${USERS_URL}/profile/purchase-history/product?userId=${userId}&productId=${productId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 5,
    }),
    decreaseLicenseOnDownload: builder.mutation({
      query: ({ productId, fileIndex }) => ({
        url: `${USERS_URL}/profile/purchase-history/product/decrease-license`,
        method: "PUT",
        body: { productId, fileIndex },
      }),
    }),
    getUsers: builder.query({
      query: () => ({
        url: USERS_URL,
      }),
      providesTags: ["Users"],
      keepUnusedDataFor: 5,
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `${USERS_URL}/${userId}`,
        method: "DELETE",
      }),
    }),
    getUserDetails: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/${userId}`,
      }),
      keepUnusedDataFor: 5,
    }),
    getUserCreator: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/profile/creator?userId=${userId}`, // Passing userId as a query parameter
      }),
      keepUnusedDataFor: 5,
    }),
    checkUserCreator: builder.query({
      query: (userId) => ({
        url: `${USERS_URL}/profile/iscreator?userId=${userId}`, // Passing userId as a query parameter
      }),
      keepUnusedDataFor: 5,
    }),
    updateUser: builder.mutation({
      query: (data) => ({
        url: `${USERS_URL}/${data.userId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Users"],
    }),
    updatePreferredCurrency: builder.mutation({
      query: (currency) => ({
        url: `${USERS_URL}/profile/payment`,
        method: "PUT",
        body: { preferredCurrency: currency },
      }),
    }),
    getUserCreatorByTag: builder.query({
      query: (tag) => ({
        url: `${USERS_URL}/creator/${tag}`, // Use tag instead of userId
      }),
      providesTags: ["Users"],
      keepUnusedDataFor: 5,
    }),
    getMyCreatorProductCategories: builder.query({
      query: () => ({
        url: `${USERS_URL}/profile/creator/categories`,
        method: "GET",
      }),
      providesTags: ["Users"],
      keepUnusedDataFor: 5,
    }),
    createCreatorProductCategory: builder.mutation({
      query: (name) => ({
        url: `${USERS_URL}/profile/creator/categories`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["Users"],
    }),
    addProductToCreatorCategory: builder.mutation({
      query: ({ categoryId, productId }) => ({
        url: `${USERS_URL}/profile/creator/categories/${categoryId}/products/${productId}`,
        method: "POST",
      }),
      invalidatesTags: ["Users", "Products"],
    }),
    removeProductFromCreatorCategory: builder.mutation({
      query: ({ categoryId, productId }) => ({
        url: `${USERS_URL}/profile/creator/categories/${categoryId}/products/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users", "Products"],
    }),
    uploadUserImage: builder.mutation({
      query: (data) => ({
        url: `${UPLOAD_URL}`,
        method: "POST",
        body: data,
      }),
    }),
    startStripeOnboarding: builder.mutation({
      query: () => ({
        url: '/api/stripe/connect/onboard',
        method: 'POST',
      }),
    }),
    syncStripeMerchantStatus: builder.mutation({
      query: () => ({
        url: '/api/stripe/connect/status',
        method: 'GET',
      }),
    }),
    saveBillingAddress: builder.mutation({
    query: (data) => ({
    url: `${USERS_URL}/billing-address`,
    method: 'POST',
    body: data,
  }),
}),
    getBillingAddress: builder.query({
      query: () => `${USERS_URL}/billing-address`,
  }),
  getBillingAddressByUserId: builder.query({
  query: (userId) => `/api/users/${userId}/billing-address`,
}),
}),
});

export const { useLoginMutation,
    useLogoutMutation,
    useRegisterMutation,
    useVerifyEmailMutation,
    useResendEmailVerificationMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useLazyValidateResetPasswordTokenQuery,
    useProfileMutation,
    useCloseAccountMutation,
    useGetProfileQuery,
    useLazyGetProfileQuery,
    useGetUserNotificationsQuery,
    useUpdateCreatorMutation,
    useGetUsersQuery,
    useDeleteUserMutation,
    useGetUserDetailsQuery,
    useUpdateUserMutation,
    useGetUserCreatorByTagQuery,
    useGetMyCreatorProductCategoriesQuery,
    useCreateCreatorProductCategoryMutation,
    useAddProductToCreatorCategoryMutation,
    useRemoveProductFromCreatorCategoryMutation,
    useGetUserCreatorQuery,
    useGetUserPurchaseHistoryQuery,
    useGetUserWishlistQuery,
    useAddProductToWishlistMutation,
    useRemoveProductFromWishlistMutation,
    useFollowCreatorMutation,
    useUnfollowCreatorMutation,
    useGetFollowingCreatorsQuery,
    useGetCreatorFollowersQuery,
    useGetUserPurchaseProductQuery,
    useDecreaseLicenseOnDownloadMutation,
    useUploadUserImageMutation,
    useUpgradeUserToCreatorMutation,
    useCheckUserCreatorQuery,
    useUpdatePreferredCurrencyMutation,
    useSaveBusinessMethodMutation,
    useDeleteBusinessStripeMethodMutation,
    useSetDefaultBusinessMethodMutation,
    useStartStripeOnboardingMutation,
    useSyncStripeMerchantStatusMutation,
    useSaveBillingAddressMutation,
    useGetBillingAddressQuery,
    useGetBillingAddressByUserIdQuery,
 } = usersApiSlice 
